import { useMemo, useState } from 'react';
import { AutoFixHigh, PersonAddAlt } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  type SourceProviderSchema,
  useCreateMigratedProviderMutation,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
} from '../store/hooks';

interface ProviderMappingPanelProps {
  organizationId: string;
  /** Import session whose provider mapping this panel edits. */
  sessionId: string;
  /** Distinct source providers referenced by the staged batch. */
  providers: SourceProviderSchema[];
  /** source provider key -> platform user id. */
  value: Record<string, string>;
  onChange: (mapping: Record<string, string>) => void;
  /** Whether encounters are in the current commit selection (drives the gate). */
  encounterSelected: boolean;
  /**
   * Suggested mapping (source provider key -> user id) read from the batch's
   * `_artifacts/provider_mapping.json` — the binding the loader stashed with the
   * batch. When present, the operator can one-click pre-fill the selects instead
   * of resolving each provider by hand.
   */
  suggestedMapping?: Record<string, string>;
  /**
   * Open this source provider's staged row in the import-review browser
   * (GDN-2412) — the other lens on the same record, where its converted data
   * and merge preview live. Omit to render no link.
   */
  onInspectProvider?: (providerKey: string) => void;
}

/** "encounter ×3, patient_diagnosis ×5" — what references this provider. */
function describeCounts(counts: SourceProviderSchema['counts']): string {
  const entries = Object.entries(counts ?? {});
  if (entries.length === 0) return '—';
  return entries.map(([type, n]) => `${type} ×${n}`).join(', ');
}

/**
 * The commit-gate's provider mapping. Each distinct source provider in the
 * batch (e.g. `provider:npi:42`) is resolved to a platform user; encounters
 * commit owned by that clinician rather than the operator. Providers marked
 * "required" are referenced by encounters and must be mapped before those
 * encounters can commit — an unmapped one blocks the row server-side.
 */
export function ProviderMappingPanel({
  organizationId,
  sessionId,
  providers,
  value,
  onChange,
  encounterSelected,
  suggestedMapping,
  onInspectProvider,
}: ProviderMappingPanelProps) {
  // The org's members — the same source the Members tab shows — are the users
  // an encounter can be attributed to. (The legacy /providers endpoint only
  // surfaces a subset, so we read membership off the org detail.) Historical
  // members (GDN-2530) are inactive but stay mappable: they exist precisely so
  // migrated records can attribute to departed clinicians.
  const { data: organization, isLoading, refetch: refetchOrganization } =
    useGetV2AdminOrganizationsByOrganizationIdQuery({ organizationId });

  const options = (organization?.users ?? []).filter(
    u => u.status !== 'inactive' || u.is_historical,
  );

  const [applyNote, setApplyNote] = useState<string | null>(null);

  // Create-migrated-provider dialog (GDN-2530): for a source clinician with no
  // JotPsych account, mint a historical member server-side and map it in one
  // call (the server merges the mapping into the session, avoiding a race with
  // this panel's whole-map autosave).
  const [createFor, setCreateFor] = useState<SourceProviderSchema | null>(null);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createMigratedProvider, { isLoading: isCreating }] =
    useCreateMigratedProviderMutation();

  const openCreateDialog = (p: SourceProviderSchema) => {
    setCreateName(
      (p as { display_name?: string | null }).display_name ?? '',
    );
    setCreateError(null);
    setCreateFor(p);
  };

  const submitCreate = async () => {
    if (!createFor) return;
    setCreateError(null);
    try {
      const result = await createMigratedProvider({
        sessionId,
        createMigratedProviderRequest: {
          organization_id: organizationId,
          provider_key: createFor.provider_key,
          name: createName.trim(),
        },
      }).unwrap();
      // The server merged the new entry into the session's persisted mapping,
      // but that snapshot can't include local edits still waiting on the
      // debounced autosave — merge into the current value rather than
      // replacing it, so a just-mapped other row isn't silently dropped (and
      // then clobbered server-side by the next autosave).
      onChange({ ...value, ...(result.provider_mapping ?? {}) });
      void refetchOrganization();
      setCreateFor(null);
    } catch {
      setCreateError(
        'Could not create the migrated provider. The provider may already be mapped — reload and check.',
      );
    }
  };

  const setOne = (providerKey: string, userId: string) => {
    const next = { ...value };
    if (userId) next[providerKey] = userId;
    else delete next[providerKey];
    onChange(next);
  };

  // Entries of the suggestion that resolve cleanly: the key is a source provider
  // in this batch AND the user id is an active member of this org. Anything else
  // (a provider not in the batch, or a user not in the org) is skipped rather
  // than written as an invalid selection.
  const applicable = useMemo(() => {
    if (!suggestedMapping) return {};
    const providerKeys = new Set(providers.map(p => p.provider_key));
    const userIds = new Set(options.map(o => o.user_id));
    const out: Record<string, string> = {};
    for (const [key, userId] of Object.entries(suggestedMapping)) {
      if (providerKeys.has(key) && userIds.has(userId)) out[key] = userId;
    }
    return out;
  }, [suggestedMapping, providers, options]);

  const suggestedCount = suggestedMapping
    ? Object.keys(suggestedMapping).length
    : 0;
  const applicableCount = Object.keys(applicable).length;

  const applySuggested = () => {
    onChange({ ...value, ...applicable });
    const skipped = suggestedCount - applicableCount;
    setApplyNote(
      `Applied ${applicableCount} mapping${applicableCount === 1 ? '' : 's'}` +
        (skipped > 0
          ? ` · ${skipped} skipped (provider not in this batch or user not in this org)`
          : ''),
    );
  };

  const unmappedRequired = providers.filter(
    p => p.required && !value[p.provider_key],
  );

  if (providers.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Map providers ({providers.length})
        </Typography>
        {suggestedCount > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoFixHigh />}
            onClick={applySuggested}
            disabled={isLoading || applicableCount === 0}
          >
            Apply suggested mapping ({applicableCount})
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Resolve each source provider to a user in this organization. Encounters
        commit owned by the mapped clinician, never the operator.
      </Typography>

      {applyNote && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setApplyNote(null)}
        >
          {applyNote}
        </Alert>
      )}

      {!isLoading && encounterSelected && unmappedRequired.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unmappedRequired.length} provider
          {unmappedRequired.length === 1 ? '' : 's'} with encounters{' '}
          {unmappedRequired.length === 1 ? 'is' : 'are'} unmapped. Map{' '}
          {unmappedRequired.length === 1 ? 'it' : 'them'} to commit those
          encounters.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source provider</TableCell>
              <TableCell>Referenced by</TableCell>
              <TableCell>Maps to</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providers.map(p => {
              const needed = encounterSelected && p.required;
              const missing = needed && !value[p.provider_key];
              // A stored mapping whose user isn't among the selectable options
              // (deactivated, or not in this org) used to render the Select
              // BLANK — indistinguishable from unmapped but not fixable at a
              // glance (GDN-2412). Surface it as an explicit error entry.
              const mappedUser = value[p.provider_key] ?? '';
              const mappedUserUnknown =
                !!mappedUser && !options.some(o => o.user_id === mappedUser);
              return (
                <TableRow key={p.provider_key} hover>
                  <TableCell>
                    {(p as { display_name?: string | null }).display_name ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {(p as { display_name?: string | null }).display_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', fontSize: 11 }}
                          color="text.secondary"
                        >
                          {p.provider_key}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontFamily: 'monospace', fontSize: 12 }}
                      >
                        {p.provider_key}
                      </Typography>
                    )}
                    {p.required && (
                      <Chip
                        size="small"
                        label="required"
                        color={missing ? 'warning' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    )}
                    {onInspectProvider && (
                      <Typography
                        variant="caption"
                        onClick={() => onInspectProvider(p.provider_key)}
                        sx={{
                          display: 'block',
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        View import row
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {describeCounts(p.counts)}
                  </TableCell>
                  <TableCell>
                    <FormControl
                      size="small"
                      sx={{ minWidth: 240 }}
                      error={missing || mappedUserUnknown}
                    >
                      <Select
                        displayEmpty
                        value={mappedUser}
                        onChange={e => setOne(p.provider_key, e.target.value)}
                        inputProps={{
                          'aria-label': `Map ${p.provider_key} to a user`,
                        }}
                      >
                        <MenuItem value="">
                          <em>Unmapped</em>
                        </MenuItem>
                        {mappedUserUnknown && (
                          <MenuItem value={mappedUser} disabled>
                            Unknown or inactive user — remap
                          </MenuItem>
                        )}
                        {options.map(o => {
                          const label = o.name || o.email || o.user_id;
                          const roles = (o.roles ?? []).join(', ');
                          return (
                            <MenuItem key={o.user_id} value={o.user_id}>
                              {roles ? `${label} — ${roles}` : label}
                              {o.is_historical && (
                                <Chip
                                  size="small"
                                  label="Historical"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    {!mappedUser && (
                      <Button
                        size="small"
                        startIcon={<PersonAddAlt />}
                        onClick={() => openCreateDialog(p)}
                        sx={{ ml: 1, whiteSpace: 'nowrap' }}
                      >
                        Create migrated provider
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={createFor !== null}
        onClose={() => !isCreating && setCreateFor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create migrated provider</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Creates a historical member for{' '}
            <Typography
              component="span"
              sx={{ fontFamily: 'monospace', fontSize: 13 }}
            >
              {createFor?.provider_key}
            </Typography>{' '}
            and maps it. Historical members can never log in or be activated —
            they exist so migrated records attribute to the right clinician.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Clinician name"
            value={createName}
            onChange={e => setCreateName(e.target.value)}
            disabled={isCreating}
          />
          {createError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFor(null)} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitCreate}
            disabled={isCreating || !createName.trim()}
          >
            {isCreating ? 'Creating…' : 'Create and map'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
